import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { currentBusinessDate } from "../../utils/businessDay";
import { PageHeader } from "../../components/common/PageHeader";
import { StatCard } from "../../components/common/StatCard";
import { StatusBadge } from "../../components/common/StatusBadge";
import { CashSettlementService } from "../../services";
import { currency, dateTime } from "../../utils/format";
import {
  AlertTriangle,
  Ban,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Loader2,
  ReceiptText,
  RefreshCw,
  Search,
  X,
  XCircle,
} from "lucide-react";

const EXPENSE_TYPES = [
  { value: "PURCHASE_BOTTLE", label: "Purchase Bottle" },
  { value: "TRANSPORT", label: "Transport" },
  { value: "OTHER", label: "Other" },
];

const PAGE_SIZE_OPTIONS = [10, 25, 50];

const todayString = () => currentBusinessDate();

const money = (value) => currency(Number(value || 0));

const safeDateTime = (value) => (value ? dateTime(value) : "-");

const buildExpenseDescription = (form) => {
  const selectedType =
    EXPENSE_TYPES.find((type) => type.value === form.expenseType)?.label ||
    "Other";

  const message = form.message.trim();

  if (form.expenseType === "OTHER") {
    return message;
  }

  return message ? `${selectedType} - ${message}` : selectedType;
};

const getDialogIcon = (type) => {
  if (type === "success") {
    return CheckCircle2;
  }

  if (type === "error") {
    return XCircle;
  }

  if (type === "warning") {
    return AlertTriangle;
  }

  return ReceiptText;
};

const getDialogIconClass = (type) => {
  if (type === "success") {
    return "text-emerald-600";
  }

  if (type === "error") {
    return "text-rose-600";
  }

  if (type === "warning") {
    return "text-amber-600";
  }

  return "text-navy-700";
};

const getPaginatedRows = (rows, page, pageSize) => {
  const total = rows.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(Math.max(page, 1), totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, total);

  return {
    pageRows: rows.slice(startIndex, endIndex),
    total,
    totalPages,
    safePage,
    startDisplay: total === 0 ? 0 : startIndex + 1,
    endDisplay: endIndex,
  };
};

const Dialog = ({ dialog, loading, onClose }) => {
  if (!dialog.open) {
    return null;
  }

  const Icon = getDialogIcon(dialog.type);
  const iconClass = getDialogIconClass(dialog.type);

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-950/50 p-0 sm:items-center sm:p-4">
      <div className="w-full rounded-t-2xl bg-white shadow-2xl sm:max-w-md sm:rounded-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 p-5">
          <div>
            <h3 className="font-display text-lg font-semibold text-slate-900">
              {dialog.title}
            </h3>

            {dialog.subtitle && (
              <p className="mt-1 text-sm text-slate-500">{dialog.subtitle}</p>
            )}
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5">
          {dialog.body || (
            <div className="flex items-start gap-3">
              <Icon className={`mt-0.5 h-6 w-6 ${iconClass}`} />
              <p className="text-sm leading-6 text-slate-700">
                {dialog.message}
              </p>
            </div>
          )}
        </div>

        <div className="flex flex-col-reverse gap-3 border-t border-slate-100 p-5 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            {dialog.cancelLabel || "Close"}
          </button>

          {dialog.onConfirm && (
            <button
              type="button"
              onClick={dialog.onConfirm}
              disabled={loading}
              className={`inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-slate-300 ${
                dialog.danger
                  ? "bg-rose-600 hover:bg-rose-700"
                  : "bg-navy-800 hover:bg-navy-900"
              }`}
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {dialog.confirmLabel || "Confirm"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, value, bold }) => (
  <div className="flex items-center justify-between gap-4">
    <span className="text-slate-500">{label}</span>
    <span
      className={
        bold
          ? "font-display font-semibold text-slate-900"
          : "text-slate-800"
      }
    >
      {value}
    </span>
  </div>
);

const PaginationFooter = ({
  page,
  pageSize,
  total,
  totalPages,
  startDisplay,
  endDisplay,
  onPageChange,
  onPageSizeChange,
}) => {
  const isFirstPage = page <= 1;
  const isLastPage = page >= totalPages;

  return (
    <div className="flex flex-col gap-3 border-t border-slate-100 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500">
        <span>Rows per page</span>

        <select
          value={pageSize}
          onChange={(event) => onPageSizeChange(Number(event.target.value))}
          className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none"
        >
          {PAGE_SIZE_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>

        <span>
          {startDisplay}–{endDisplay} of {total}
        </span>
      </div>

      <div className="flex items-center justify-end gap-2 text-sm">
        <button
          type="button"
          onClick={() => onPageChange(1)}
          disabled={isFirstPage}
          className="rounded-lg border border-slate-200 bg-white p-2 text-slate-500 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
          aria-label="First page"
        >
          <ChevronsLeft className="h-4 w-4" />
        </button>

        <button
          type="button"
          onClick={() => onPageChange(page - 1)}
          disabled={isFirstPage}
          className="rounded-lg border border-slate-200 bg-white p-2 text-slate-500 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
          aria-label="Previous page"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>

        <span className="min-w-[88px] text-center font-medium text-slate-700">
          Page {page} / {totalPages}
        </span>

        <button
          type="button"
          onClick={() => onPageChange(page + 1)}
          disabled={isLastPage}
          className="rounded-lg border border-slate-200 bg-white p-2 text-slate-500 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
          aria-label="Next page"
        >
          <ChevronRight className="h-4 w-4" />
        </button>

        <button
          type="button"
          onClick={() => onPageChange(totalPages)}
          disabled={isLastPage}
          className="rounded-lg border border-slate-200 bg-white p-2 text-slate-500 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
          aria-label="Last page"
        >
          <ChevronsRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

const VoidReasonForm = ({ expense, onSubmit }) => {
  const [reason, setReason] = useState("");

  return (
    <div className="space-y-4">
      <div className="rounded-lg bg-slate-50 p-4">
        <div className="text-sm text-slate-500">Expense</div>
        <div className="mt-1 font-display text-2xl font-semibold text-slate-900">
          {money(expense.amount)}
        </div>
        <div className="mt-1 text-sm text-slate-600">
          {expense.description}
        </div>
      </div>

      <div>
        <label className="text-sm font-medium text-slate-700">
          Void reason
        </label>
        <textarea
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          rows={3}
          className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-rose-500 focus:ring-2 focus:ring-rose-100"
          placeholder="Reason for voiding this expense"
        />
      </div>

      <button
        type="button"
        onClick={() => onSubmit(reason.trim())}
        disabled={!reason.trim()}
        className="w-full rounded-lg bg-rose-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-rose-700 disabled:cursor-not-allowed disabled:bg-slate-300"
      >
        Void Expense
      </button>
    </div>
  );
};

export const CashierExpensesPage = () => {
  const [searchParams] = useSearchParams();
  const businessDate = searchParams.get("businessDate") || currentBusinessDate();
  const [expenseHistory, setExpenseHistory] = useState([]);
  const [expenseForm, setExpenseForm] = useState({
    expenseType: "PURCHASE_BOTTLE",
    amount: "",
    message: "",
  });

  const [expenseFilters, setExpenseFilters] = useState({
    fromDate: businessDate,
    toDate: businessDate,
    status: "ALL",
    search: "",
  });

  const [expensePage, setExpensePage] = useState(1);
  const [expensePageSize, setExpensePageSize] = useState(10);

  const [loading, setLoading] = useState(true);
  const [actionLoadingKey, setActionLoadingKey] = useState("");
  const [dialog, setDialog] = useState({ open: false });

  const closeDialog = () => {
    setDialog({ open: false });
  };

  const showMessage = (title, message, type = "info") => {
    setDialog({
      open: true,
      title,
      message,
      type,
      cancelLabel: "OK",
    });
  };

  const loadExpenseHistory = useCallback(async () => {
    setLoading(true);

    try {
      const data = await CashSettlementService.expenses({
        fromDate: expenseFilters.fromDate,
        toDate: expenseFilters.toDate,
        search: expenseFilters.search,
        businessDate,
      });

      setExpenseHistory(data || []);
    } catch (error) {
      showMessage(
        "Unable to load expenses",
        error?.response?.data?.message ||
          error?.message ||
          "Please refresh and try again.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  }, [expenseFilters.fromDate, expenseFilters.toDate, expenseFilters.search, businessDate]);

  useEffect(() => {
    loadExpenseHistory();
  }, [loadExpenseHistory]);

  const filteredExpenseHistory = useMemo(() => {
    return (expenseHistory || []).filter((row) => {
      if (
        expenseFilters.status !== "ALL" &&
        row.status !== expenseFilters.status
      ) {
        return false;
      }

      return true;
    });
  }, [expenseHistory, expenseFilters.status]);

  const activeBarExpenseTotal = useMemo(() => {
    return (expenseHistory || [])
      .filter((row) => row.status === "ACTIVE")
      .reduce((sum, row) => sum + Number(row.amount || 0), 0);
  }, [expenseHistory]);

  const voidedBarExpenseTotal = useMemo(() => {
    return (expenseHistory || [])
      .filter((row) => row.status === "VOIDED")
      .reduce((sum, row) => sum + Number(row.amount || 0), 0);
  }, [expenseHistory]);

  const expensePagination = getPaginatedRows(
    filteredExpenseHistory,
    expensePage,
    expensePageSize
  );

  useEffect(() => {
    if (expensePage > expensePagination.totalPages) {
      setExpensePage(expensePagination.totalPages);
    }
  }, [expensePage, expensePagination.totalPages]);

  useEffect(() => {
    setExpensePage(1);
  }, [expenseFilters.status, expenseFilters.search]);

  const submitExpense = (event) => {
    event.preventDefault();

    const amount = Number(expenseForm.amount || 0);
    const description = buildExpenseDescription(expenseForm);

    if (amount <= 0) {
      showMessage(
        "Invalid amount",
        "Expense amount must be greater than zero.",
        "warning"
      );
      return;
    }

    if (expenseForm.expenseType === "OTHER" && !expenseForm.message.trim()) {
      showMessage(
        "Message required",
        "Please enter a message for Other expense.",
        "warning"
      );
      return;
    }

    setDialog({
      open: true,
      title: "Add Bar Expense",
      subtitle: "This reduces the owner payable amount.",
      body: (
        <div className="space-y-3">
          <Row label="Type" value={description} />
          <Row label="Amount" value={money(amount)} bold />
        </div>
      ),
      confirmLabel: "Add Expense",
      cancelLabel: "Cancel",
      onConfirm: async () => {
        setActionLoadingKey("expense");

        try {
          await CashSettlementService.addExpense({
            expenseScope: "BAR",
            amount,
            description,
            businessDate,
          });

          setExpenseForm({
            expenseType: "PURCHASE_BOTTLE",
            amount: "",
            message: "",
          });

          closeDialog();
          showMessage(
            "Expense added",
            "The expense was saved successfully.",
            "success"
          );

          await loadExpenseHistory();
        } catch (error) {
          showMessage(
            "Expense save failed",
            error?.response?.data?.message ||
              error?.message ||
              "Could not save expense.",
            "error"
          );
        } finally {
          setActionLoadingKey("");
        }
      },
    });
  };

  const voidExpense = (expense) => {
    setDialog({
      open: true,
      title: "Void Expense",
      subtitle:
        "Use this when the expense was entered incorrectly. Add the correct expense again after voiding.",
      danger: true,
      body: (
        <VoidReasonForm
          expense={expense}
          onSubmit={async (reason) => {
            setActionLoadingKey(`void-${expense.expenseId}`);

            try {
              await CashSettlementService.voidExpense(expense.expenseId, reason);

              closeDialog();
              showMessage(
                "Expense voided",
                "The expense was voided successfully.",
                "success"
              );

              await loadExpenseHistory();
            } catch (error) {
              showMessage(
                "Void failed",
                error?.response?.data?.message ||
                  error?.message ||
                  "Could not void expense.",
                "error"
              );
            } finally {
              setActionLoadingKey("");
            }
          }}
        />
      ),
      cancelLabel: "Close",
    });
  };

  return (
    <div>
      <PageHeader
        title="Bar Expenses"
        subtitle="Add, search, filter, and void bar expenses. Default filter date is today."
        actions={
          <button
            type="button"
            onClick={loadExpenseHistory}
            disabled={loading}
            className="px-5 py-2.5 rounded-lg text-sm border border-slate-200 bg-white hover:bg-slate-50 inline-flex items-center gap-2 disabled:opacity-60"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Refresh
          </button>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard
          label="Active Expenses"
          value={loading ? "…" : money(activeBarExpenseTotal)}
          icon="Receipt"
          tone="gold"
        />
        <StatCard
          label="Voided Expenses"
          value={loading ? "…" : money(voidedBarExpenseTotal)}
          icon="Receipt"
          tone="rose"
        />
        <StatCard
          label="Selected Date"
          value={`${expenseFilters.fromDate} to ${expenseFilters.toDate}`}
          icon="Calendar"
          tone="navy"
        />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-[420px_1fr]">
        <div className="bms-card p-5">
          <h3 className="font-display text-lg font-semibold">Add Bar Expense</h3>
          <p className="mt-2 text-sm text-slate-500">
            Bar expenses reduce the owner payable amount.
          </p>

          <form onSubmit={submitExpense} className="mt-5 space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700">
                Expense type
              </label>
              <select
                value={expenseForm.expenseType}
                onChange={(event) =>
                  setExpenseForm((prev) => ({
                    ...prev,
                    expenseType: event.target.value,
                    message: event.target.value === "OTHER" ? "" : prev.message,
                  }))
                }
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-navy-500 focus:ring-2 focus:ring-navy-100"
              >
                {EXPENSE_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700">Amount</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={expenseForm.amount}
                onChange={(event) =>
                  setExpenseForm((prev) => ({
                    ...prev,
                    amount: event.target.value,
                  }))
                }
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-navy-500 focus:ring-2 focus:ring-navy-100"
                placeholder="0.00"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700">
                {expenseForm.expenseType === "OTHER"
                  ? "Message"
                  : "Message optional"}
              </label>
              <input
                type="text"
                value={expenseForm.message}
                onChange={(event) =>
                  setExpenseForm((prev) => ({
                    ...prev,
                    message: event.target.value,
                  }))
                }
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-navy-500 focus:ring-2 focus:ring-navy-100"
                placeholder={
                  expenseForm.expenseType === "OTHER"
                    ? "Explain the expense"
                    : "Extra note"
                }
              />
            </div>

            <button
              type="submit"
              disabled={actionLoadingKey === "expense"}
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-navy-800 px-5 py-2.5 text-sm font-semibold text-white hover:bg-navy-900 disabled:cursor-not-allowed disabled:bg-slate-300"
            >
              {actionLoadingKey === "expense" ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ReceiptText className="h-4 w-4" />
              )}
              {actionLoadingKey === "expense" ? "Adding..." : "Add Expense"}
            </button>
          </form>
        </div>

        <div className="bms-card p-5">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
            <div>
              <h3 className="font-display text-lg font-semibold">
                Expense History
              </h3>
              <p className="mt-1 text-sm text-slate-500">
                Date filters are selected to today by default.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-[140px_140px_140px_220px_auto]">
              <input
                type="date"
                value={expenseFilters.fromDate}
                onChange={(event) =>
                  setExpenseFilters((prev) => ({
                    ...prev,
                    fromDate: event.target.value,
                  }))
                }
                className="rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none"
              />

              <input
                type="date"
                value={expenseFilters.toDate}
                onChange={(event) =>
                  setExpenseFilters((prev) => ({
                    ...prev,
                    toDate: event.target.value,
                  }))
                }
                className="rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none"
              />

              <select
                value={expenseFilters.status}
                onChange={(event) =>
                  setExpenseFilters((prev) => ({
                    ...prev,
                    status: event.target.value,
                  }))
                }
                className="rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none"
              >
                <option value="ALL">All Status</option>
                <option value="ACTIVE">Active</option>
                <option value="VOIDED">Voided</option>
              </select>

              <div className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2">
                <Search className="h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  value={expenseFilters.search}
                  onChange={(event) =>
                    setExpenseFilters((prev) => ({
                      ...prev,
                      search: event.target.value,
                    }))
                  }
                  className="w-full bg-transparent text-sm outline-none"
                  placeholder="Search"
                />
              </div>

              <button
                type="button"
                onClick={() => {
                  setExpensePage(1);
                  loadExpenseHistory();
                }}
                className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium hover:bg-slate-50"
              >
                Apply
              </button>
            </div>
          </div>

          <div className="mt-5 overflow-hidden rounded-xl border border-slate-200">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-100 text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-slate-500">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-slate-500">
                      Reason
                    </th>
                    <th className="px-4 py-3 text-right font-medium text-slate-500">
                      Amount
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-slate-500">
                      Status
                    </th>
                    <th className="px-4 py-3 text-right font-medium text-slate-500">
                      Action
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-100 bg-white">
                  {expensePagination.pageRows.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                        No expenses found for the selected date.
                      </td>
                    </tr>
                  ) : (
                    expensePagination.pageRows.map((row) => (
                      <tr key={row.expenseId} className="hover:bg-slate-50/70">
                        <td className="px-4 py-3 text-slate-700">
                          {safeDateTime(row.expenseDateTime)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-slate-900">
                            {row.description}
                          </div>
                          {row.voidReason && (
                            <div className="mt-1 text-xs text-rose-600">
                              Void reason: {row.voidReason}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right font-semibold">
                          {money(row.amount)}
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge value={row.status} />
                        </td>
                        <td className="px-4 py-3 text-right">
                          {row.status === "ACTIVE" ? (
                            <button
                              type="button"
                              onClick={() => voidExpense(row)}
                              className="inline-flex items-center gap-1 rounded-lg border border-rose-200 px-3 py-1.5 text-xs font-medium text-rose-700 hover:bg-rose-50"
                            >
                              <Ban className="h-3 w-3" />
                              Void
                            </button>
                          ) : (
                            <span className="text-xs text-slate-400">No action</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <PaginationFooter
              page={expensePagination.safePage}
              pageSize={expensePageSize}
              total={expensePagination.total}
              totalPages={expensePagination.totalPages}
              startDisplay={expensePagination.startDisplay}
              endDisplay={expensePagination.endDisplay}
              onPageChange={setExpensePage}
              onPageSizeChange={(value) => {
                setExpensePageSize(value);
                setExpensePage(1);
              }}
            />
          </div>
        </div>
      </div>

      <Dialog
        dialog={dialog}
        loading={Boolean(actionLoadingKey)}
        onClose={closeDialog}
      />
    </div>
  );
};
